import { FC, useEffect, useState } from "react";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useSession } from "next-auth/react";
import { Heading, Text } from "@otrium/atoms";
import { Flex } from "@otrium/core";
import { theme } from "src/theme";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { Modal } from "src/atoms/Modal";
import { Button } from "src/atoms/Button";
import { CloseButton } from "src/atoms/CloseButton";
import Input from "src/atoms/Input";
import { Price } from "src/atoms/Price";
import { SingleProduct } from "src/types/graphql.d";
import { SizeMapping } from "src/molecules/SizeSelector";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { CheckCircleIcon } from "src/icons/CheckCircleIcon";
import { BellRinging } from "src/icons/BellRinging";
import { trackBackInStockNotificationRequestedEvent } from "src/tracking/events/back-in-stock-notification-requested";
import {
  PopupContainer,
  MobilePopupContainer,
  PopupHeader,
  ProductInfoWrapper,
  ProductImage,
  ProductDetails,
  FormWrapper,
  ButtonWrapper,
  DesktopPopupWrapper,
  MobilePopupWrapper,
  MobileOverlay,
  SuccessToast,
} from "./BackInStockNotification.styled";

interface FormValues {
  email: string;
}

interface BackInStockNotificationProps {
  isOpen: boolean;
  onClose: () => void;
  product: SingleProduct;
  sizeMapping: SizeMapping;
  selectedSize: string;
  isMobile: boolean;
}

const BackInStockNotificationContent: FC<{
  product: SingleProduct;
  sizeMapping: SizeMapping;
  selectedSize: string;
  onClose: () => void;
  initialEmail: string;
  isLoggedIn: boolean;
  isMobile?: boolean;
}> = ({
  product,
  sizeMapping,
  selectedSize,
  onClose,
  initialEmail,
  isLoggedIn,
  isMobile,
}) => {
  const { _, i18n } = useLingui();
  const [isSuccess, setIsSuccess] = useState(false);

  const productImage = product.images?.[0] || "";

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const timer = setTimeout(() => {
      onClose();
    }, 2000);

    return () => clearTimeout(timer);
  }, [isSuccess, onClose]);

  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    initialValues: {
      email: initialEmail,
    },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .required(FORM_ERROR_MESSAGES.emailRequired)
        .email(FORM_ERROR_MESSAGES.email),
    }),
    onSubmit: async (values) => {
      void trackBackInStockNotificationRequestedEvent({
        product,
        variationId: sizeMapping.variationId,
        email: values.email,
      });
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsSuccess(true);
    },
  });

  const Container = isMobile ? MobilePopupContainer : PopupContainer;

  return (
    <Container>
      <PopupHeader>
        <Heading
          as="h2"
          fontFamily="PlusJakartaSans"
          fontSize={20}
          fontWeight={700}
          lineHeight="24px"
          color="tone.black"
        >
          <Trans>Notify me when available</Trans>
        </Heading>
        <CloseButton
          onClick={onClose}
          iconSize={24}
          sx={{ padding: 0, justifyContent: "end" }}
        />
      </PopupHeader>

      <ProductInfoWrapper>
        <ProductImage src={productImage} alt={product.name} />
        <ProductDetails>
          <Heading as="h3" fontSize={16} fontWeight={600} lineHeight={1.5}>
            {product.brand.name}
          </Heading>
          <Text fontSize={14}>{product.name}</Text>
          <Text fontSize={14} color="tone.black">
            <Trans>Size</Trans>: {selectedSize}
          </Text>
          <Flex alignItems="center" sx={{ gap: 8 }}>
            <Text fontSize={14} fontWeight={600} color="tone.black">
              <Price price={product.price} />
            </Text>
            <Text fontSize={14} color="tone.anthracite">
              <Price price={product.regular_price} />
              {"*"}
            </Text>
          </Flex>
        </ProductDetails>
      </ProductInfoWrapper>

      <FormWrapper>
        <form noValidate onSubmit={formik.handleSubmit}>
          <Input
            {...formik.getFieldProps("email")}
            type="email"
            label={formik.values.email ? t(i18n)`Email` : ""}
            placeholder={t(i18n)`Email`}
            name="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            isError={formik.touched.email && formik.errors.email !== undefined}
            errorMessage={
              formik.touched.email && formik.errors.email
                ? _(formik.errors.email)
                : undefined
            }
            disabled={isLoggedIn}
            fullWidth
          />
          <ButtonWrapper isMobile={isMobile}>
            {isSuccess ? (
              <SuccessToast>
                <CheckCircleIcon
                  width={20}
                  height={20}
                  color={theme.colors.semantic.green}
                />
                <Trans>
                  We&apos;ll notify you when it&apos;s back in stock.
                </Trans>
              </SuccessToast>
            ) : (
              <Button
                disabled={formik.isSubmitting}
                variant="primary"
                type="submit"
                fullWidth
                loading={formik.isSubmitting}
                leftIcon={<BellRinging color="white" />}
              >
                <Trans>Notify me</Trans>
              </Button>
            )}
          </ButtonWrapper>
        </form>
      </FormWrapper>
    </Container>
  );
};

export const BackInStockNotification: FC<BackInStockNotificationProps> = ({
  isOpen,
  onClose,
  product,
  sizeMapping,
  selectedSize,
  isMobile,
}) => {
  const { data: session } = useSession();
  const isLoggedIn = Boolean(session?.user?.email);
  const initialEmail = session?.user?.email || "";

  if (isMobile) {
    return (
      <>
        <MobileOverlay isOpen={isOpen} onClick={onClose} />
        <MobilePopupWrapper
          data-dropdown-panel
          onClick={(e) => e.stopPropagation()}
        >
          <BackInStockNotificationContent
            product={product}
            sizeMapping={sizeMapping}
            selectedSize={selectedSize}
            onClose={onClose}
            initialEmail={initialEmail}
            isLoggedIn={isLoggedIn}
            isMobile
          />
        </MobilePopupWrapper>
      </>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      centerOnScreen
      fullHeight={false}
      rounded
    >
      <DesktopPopupWrapper
        data-dropdown-panel
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <BackInStockNotificationContent
          product={product}
          sizeMapping={sizeMapping}
          selectedSize={selectedSize}
          onClose={onClose}
          initialEmail={initialEmail}
          isLoggedIn={isLoggedIn}
        />
      </DesktopPopupWrapper>
    </Modal>
  );
};

export default BackInStockNotification;
